import CreativelyLogo from 'images/index/creatively.svg'
import LiveLogo from 'images/index/live.svg'
import MustardLogo from 'images/index/mustard-full.svg'
import CreativelyHero from 'images/projects/creatively/creatively-hero.avif'
import Live from 'images/projects/live/live-seo.webp'
import Mustard from 'images/projects/mustard/mustard-seo.webp'

import {
  CREATIVELY_PROJECT_ROUTE,
  MUSTARD_PROJECT_ROUTE,
  LIVE_PROJECT_ROUTE,
} from 'routes/constants'
import { THEME_WHITE } from 'theme/constants'
import {
  HERO_TWO,
  TWO_COLUMN,
  FEATURED_PROJECTS,
  INLINE_BUTTON_HEADER,
} from 'components/SEM/constants'

export const seoPageData = {
  theme: THEME_WHITE,
  layout: [
    {
      type: HERO_TWO,
      heading: 'Fintech App Development Company',
      subheading: '',
      buttonText: 'Build with us',
      isCentered: true,
      hasArrow: true,
    },
    {
      type: TWO_COLUMN,
      hasPaddingTop: true,
      hasNoPaddingBottom: true,
      paragraph: [
        "You're not here for a fintech 101 lesson; you're here to revolutionize the financial sector.",
        "Whether your arena is Banking, Payments, Financial Planning, Investments, Lending, Insurance, or navigating the intricate regulatory frameworks, you're likely interested in fintech app development. You might be:",
      ],
      unorderedList: [
        'An entrepreneur with a vision, ready to disrupt the market with an innovative MVP or version one.',
        "A well-funded startup looking to elevate your product's user experience, perhaps taking a successful web app into the mobile frontier.",
        "An enterprise infusing a startup's agility and innovative spirit into new feature sets, or launching a novel concept to captivate a fresh user demographic.",
      ],
    },
    {
      type: TWO_COLUMN,
      paragraph: [
        "The financial sector's evolution has been rapid, transitioning from the physical to the digital, and now, towards seamless integration within the software that businesses and consumers engage with daily. And with the advent of crypto and Web3, the narrative continues, promising a shift from centralized systems to a decentralized ecosystem over the coming decades.",
      ],
    },
    {
      type: TWO_COLUMN,
      heading: {
        size: 'small',
        text: 'Customer-Facing Fintech App Development',
      },
      paragraph: [
        'Each fintech app will be unique, but all of them should contain a core suite of essential, customer-facing features:',
      ],
      columns: [
        {
          heading: 'Effortless Account Management',
          description: [
            'In fintech app development, the starting block is facilitating smooth account management. Users bring their banking essentials to the table; the app brings simplicity and ease of navigation. A few taps should be all it takes for users to manage their accounts, reflecting a commitment to convenience and efficiency.',
          ],
        },
        {
          heading: 'Seamless Access on the Go',
          description: [
            'Mobility is non-negotiable. Users expect to access services swiftly, without friction. Streamlined access to critical features is a testament to well-thought-out fintech mobile app development that respects the user’s time and intent.',
          ],
        },
        {
          heading: 'Customization at Its Core',
          description: [
            'The power of choice in display and functionality personalizes the fintech experience. Whether it’s a minimalist dashboard or a detailed analytical view, customization caters to individual preferences, making every interaction feel tailored and relevant.',
          ],
        },
        {
          heading: 'Visual Clarity',
          description: [
            'Numbers matter, but the visualization of financial data is what most users will appreciate daily. An at-a-glance understanding of one’s financial status through well-designed charts and color-coded indicators makes complex data digestible.',
          ],
        },
        {
          heading: 'Engaging Alerts and Notifications',
          description: [
            'Proactive communication through alerts and notifications keeps users informed and engaged. It’s about delivering the right message at the right time, from account updates to timely financial opportunities.',
          ],
        },
        {
          heading: 'Support Through AI',
          description: [
            'Chatbots embody the intersection of technology and customer service. They’re a useful feature for a fintech app development company to leverage that provides instant, round-the-clock support for everyday inquiries, driving down costs, and reserving human expertise for complex issues.',
          ],
        },
        {
          heading: 'Inclusive Design',
          description: [
            'Accessible fintech app design prioritizes clarity and simplicity. This also comes from in-depth onboarding and customizable interfaces for all users, without compromising privacy. The design should ensure that every user, regardless of their expertise, can navigate confidently and securely, with helpful guides and clear communication.',
          ],
        },
        {
          heading: 'Enhanced Safety',
          description: [
            'Fintech app development hinges on robust security measures like secure coding, encrypted data transmission, and blockchain, ensuring comprehensive protection for user data. Transparent communication through design elements and informative pop-ups reinforces user confidence in the safety of their financial information and supports their decision-making.',
          ],
        },
      ],
    },
    {
      type: TWO_COLUMN,
      heading: {
        size: 'small',
        text: 'Internal or Admin Tools and Features',
      },
      paragraph: [
        'Within the unique framework of each fintech application lies a critical array of internal/admin features that form the backbone of operational efficiency and service delivery.',
      ],
      columns: [
        {
          heading: 'Robust Admin Portal',
          description: [
            "At the core of every fintech web application is a powerful admin portal, designed to act as the control room for the platform's operations. It's where the oversight of user registrations and transactions occurs, granting administrators the ability to manage and facilitate essential financial activities seamlessly.",
          ],
        },
        {
          heading: 'User and Transaction Management',
          description: [
            'Administrators wield the tools to monitor and guide user interactions, handling everything from individual transactions to complex, recurring financial operations. This level of control is crucial in fintech app development for maintaining the integrity and fluidity of the financial platform.',
          ],
        },
        {
          heading: 'Content Management System',
          description: [
            'The admin portal also functions as a content management hub, organizing and structuring the plethora of documents and content that populate the fintech application. This ensures that both users and administrators can access and utilize information efficiently.',
          ],
        },
        {
          heading: 'Insightful Reporting and Dashboards',
          description: [
            "Integral to the admin experience are the reporting and dashboard functionalities, which provide a window into the platform's analytics. These insights reveal user behaviors, financial trends, and patterns, equipping administrators with the knowledge to make data-driven enhancements to the service.",
          ],
        },
        {
          heading: 'Streamlining with Automation',
          description: [
            'Automation is the driving force behind expedited data processing, reducing bottlenecks in critical workflows and delivering a superior customer experience by increasing application efficiency.',
          ],
        },
        {
          heading: 'Data Analytics and Executive Dashboards',
          description: [
            "Post-launch, the app's usage data—collected and analyzed via cloud services—becomes a gold mine for continuous improvement. Fintech app development services should always include this key feature because it offers business analysts a treasure trove of actionable insights.",
          ],
        },
        {
          heading: 'Tiered Security Protocols',
          description: [
            'Security is paramount when handling sensitive financial data. Implementing multi-tier authentication measures, including biometrics and two-factor authentication, fortifies the app against unauthorized access and potential fraud.',
          ],
        },
        {
          heading: 'AI-Driven Personalization',
          description: [
            'AI technology is adept at parsing vast quantities of data to personalize user experiences. A fintech app development company that leverages AI lets you take advantage of predictive recommendations, enhancing user engagement through deep personalization.',
          ],
        },
        {
          heading: 'Banking Integration',
          description: [
            "Despite fintech's rise, its synergy with traditional banking remains essential. Deep integration with bank APIs allows fintech apps to access extensive records, from transaction histories to loan data, enriching the user's financial landscape.",
          ],
        },
      ],
    },
    {
      type: TWO_COLUMN,
      heading: {
        size: 'small',
        text: 'Back-End and API Considerations',
      },
      paragraph: [
        "In fintech app development, integration isn't just a feature—it's the cornerstone that connects the different financial technologies. From APIs and middleware platforms to core banking systems and payment gateways, each element must work together to deliver a seamless financial service experience.",
      ],
    },
    {
      type: TWO_COLUMN,
      hasNoPaddingBottom: true,
      heading: {
        size: 'small',
        text: 'API Integration',
      },
      paragraph: [
        'APIs are vital in fintech for enabling seamless interactions between diverse financial systems. They facilitate essential operations like account access, transaction initiation, and financial data retrieval, creating a cohesive digital financial environment.',
        'There are 5 types of APIs that fintech apps can use for various tasks, and each has some leading examples of providers:',
      ],
      unorderedList: [
        '<b>Authentication/Verification:</b> Yodlee, for example, offers a robust financial data platform that spans across banking and credit sectors, facilitating behavior analysis and fraud reduction.',
        '<b>B2B Payments:</b> Invoicera facilitates both one-time and recurring business payments.',
        '<b>Remittance and Money Transfer:</b> Uphold’s API enables low-cost, multi-currency transactions including cryptocurrencies.',
        '<b>Billing and Payments:</b> GoCardless simplifies direct debit transactions.',
        '<b>Trading APIs:</b> Oanda’s API provides developers with access to its fxTrade platform for forex and other financial instruments trading.',
      ],
    },
    {
      type: TWO_COLUMN,
      hasNoPaddingTop: true,
      columns: [
        {
          heading: 'Middleware, Payment, and Core Banking Integration',
          description: [
            'Middleware platforms are the harmonizing layer that connects various components within the fintech ecosystem, ensuring fluid data exchange. Integrating with core banking systems and payment gateways is crucial for leveraging existing infrastructures and providing secure, real-time transaction capabilities.',
          ],
        },
        {
          heading: 'Strategic Integration Selection',
          description: [
            'A fintech app development company should be equipped to select the right integration solutions. Compatibility with current systems, scalability for future growth, and robust security protocols are non-negotiables. User-friendliness and streamlined third-party integrations enhance functionality, while cost-efficiency and a solid ROI keep business objectives in focus. A strategic approach to these back-end considerations ensures that your fintech app not only functions but flourishes in the dynamic world of digital finance.',
          ],
        },
      ],
    },
    {
      type: TWO_COLUMN,
      heading: {
        size: 'small',
        text: 'Blockchain and DeFi Applications in Fintech',
      },
      paragraph: [
        'Distributed Ledger Technology (DLT) and blockchain technologies promise enhanced security and transparency by enabling an immutable ledger that synchronizes data across a network. Fintech apps leveraging blockchain can offer features like real-time transactions and secure, decentralized asset management. The skillful integration of DeFi within fintech app development services is rapidly expanding, with its ability to eliminate intermediaries and facilitate direct peer-to-peer financial interactions.',
        "As fintech app developers harness blockchain's potential, they are also navigating the challenges of integrating with traditional banking systems and adapting to emerging regulations. The surge in the development of Central Bank Digital Currencies (CBDC) by central banks exemplifies the intersection of policy and innovation. Fintech app development companies are increasingly providing solutions for secure digital transactions, smart contract-enabled services, and user authentication bolstered by zero-knowledge proofs. These advancements are setting the stage for a financial ecosystem that is both accessible and resilient, inviting a future where fintech apps are integral to everyday financial activities and investment strategies.",
      ],
    },
    {
      type: TWO_COLUMN,
      heading: {
        size: 'small',
        text: 'Using AI in Fintech Mobile Application Development',
      },
      paragraph: [
        'AI stands as a transformative force in fintech, with McKinsey estimating its potential to add up to $1 trillion in value annually to the global banking sector. Using AI, companies can refine financial modeling and leverage knowledge graphs for complex data analysis. Privacy-enhancing analytics and decentralized learning are also emerging to train financial models responsibly, minimizing data use and protecting consumer privacy. With AI applications spanning all operational facets, from customer interactions to fraud detection, the financial industry is shifting towards using this technology to unlock the full potential of customer data and drive ecosystem-based financing.',
        'To responsibly design and deploy generative AI in mobile apps, fintech app development companies are investing in five principles:',
      ],
      unorderedList: [
        '<b>Human-Centric Design:</b> AI systems should be transparent, explainable, and auditable, ensuring fairness and the possibility for human intervention if needed.',
        '<b>Robust Data Privacy and Infrastructure:</b> Choose the right AI model and provider to align with your data security, privacy, and infrastructure needs, ensuring flexibility for future changes.',
        '<b>Proactive Regulation Preparedness:</b> Stay ahead by actively engaging with emerging regulations and embedding robust governance and risk management practices.',
        '<b>Agile Oversight and Continuous Disclosure:</b> Continuously monitor AI applications post-deployment for risks and maintain criteria for rigorous testing and evaluation.',
        '<b>Intentional Application Selection:</b> Start with low-risk AI applications and gradually progress to higher-risk scenarios as organizational AI maturity and governance grow.',
      ],
    },
    {
      type: TWO_COLUMN,
      paragraph: [
        'The actual applications of AI in fintech are not just changing how data is managed, but also how customers and financial institutions interact. AI is offering a level of personalization and efficiency previously unattainable in fintech app development, in ways such as:',
      ],
      unorderedList: [
        '<b>Personalized Customer Engagement:</b> AI is shifting the focus from momentary ROI to enhancing the entire customer experience, with surveys showing a significant NPS increase for banks that offer personalized interactions.',
        "<b>AI-Enhanced Digital Assistance:</b> The Royal Bank of Canada's NOMI and Morgan Stanley's AI assistant exemplify how digital assistants can provide personalized financial advice and customer support, leading to increased customer retention and satisfaction.",
        "<b>Behavioral Fingerprinting:</b> Advanced AI techniques are creating unique behavioral fingerprints from customers' digital interactions, including speech and text, for unprecedented personalization.",
        '<b>Supportive AI for Employees:</b> AI is aiding financial advisors and customer service agents by providing next-step suggestions and real-time support, improving the quality of customer interactions.',
        '<b>Predictive Customer Service:</b> Financial institutions are using AI to predictively route customer inquiries and offer real-time guidance to agents, enhancing decision-making and customer satisfaction.',
      ],
    },
    {
      type: INLINE_BUTTON_HEADER,
      heading: 'Selected Projects',
      buttonText: 'Join them',
      hasButton: true,
    },
    {
      type: FEATURED_PROJECTS,
      hasFooterButtonMobile: true,
      hasNoPaddingBottom: true,
      buttonText: 'Join them',
      projects: [
        {
          key: 'creatively',
          title: 'Creatively',
          description: 'The job platform for creatives',
          logo: CreativelyLogo,
          image: CreativelyHero,
          route: CREATIVELY_PROJECT_ROUTE,
        },
        {
          key: 'mustard',
          title: 'Mustard',
          description: 'Experience the next generation of motion tracking',
          logo: MustardLogo,
          image: Mustard,
          route: MUSTARD_PROJECT_ROUTE,
        },
        {
          key: 'live',
          title: 'Live',
          description: 'Experience the next generation of luxury retail',
          logo: LiveLogo,
          image: Live,
          route: LIVE_PROJECT_ROUTE,
        },
      ],
    },
  ],
}
